/**
 * InitialEvalForm — structured evaluation template rendered inside
 * SessionPlannerScreen when session_type = 'initial_eval' or 'reeval'.
 *
 * Replaces the therapy planner (focus goals + AI generate + activities)
 * with the canonical 9-section pediatric SLP evaluation form.
 *
 * All data is stored in sessions.eval_data as a single JSONB blob.
 * The form is clinician-authored (no AI generation) — each section
 * is a textarea with a label and optional sub-fields.
 *
 * v1: read/write. The clinician fills in what they have; empty
 * sections are simply omitted from the saved JSON.
 */
import { useState } from 'react';
import type { ReactElement } from 'react';
import type { InitialEvaluation } from '../lib/effectiveslp/eval';
import { CheckIcon } from '../components/icons/CheckIcon';

interface Props {
  initialData: InitialEvaluation | null;
  onSave: (data: InitialEvaluation) => Promise<void>;
  saving: boolean;
  saved: boolean;
}

type EvalSectionKey = keyof InitialEvaluation;

interface SectionDef {
  key: EvalSectionKey;
  title: string;
  fields: { key: string; label: string; multiline?: boolean; type?: 'text' | 'number' | 'list' }[];
}

const SECTIONS: SectionDef[] = [
  {
    key: 'referral',
    title: 'Referral',
    fields: [
      { key: 'source', label: 'Referral source' },
      { key: 'reason', label: 'Reason for referral', multiline: true },
    ],
  },
  {
    key: 'background',
    title: 'Background / Case History',
    fields: [
      { key: 'birth_history', label: 'Birth history', multiline: true },
      { key: 'medical_history', label: 'Medical history', multiline: true },
      { key: 'developmental', label: 'Developmental milestones', multiline: true },
      { key: 'educational', label: 'Educational history', multiline: true },
      { key: 'family_history', label: 'Family history of speech/language/learning disorders', multiline: true },
      { key: 'parent_concerns', label: 'Parent/teacher concerns', multiline: true },
    ],
  },
  {
    key: 'screenings',
    title: 'Hearing, Vision & Oral Mechanism',
    fields: [
      { key: 'hearing', label: 'Hearing screening' },
      { key: 'vision', label: 'Vision screening' },
      { key: 'oral_mechanism', label: 'Oral mechanism exam', multiline: true },
    ],
  },
  {
    key: 'speech',
    title: 'Speech Assessment',
    fields: [
      { key: 'articulation', label: 'Articulation', multiline: true },
      { key: 'phonology', label: 'Phonology', multiline: true },
      { key: 'voice', label: 'Voice' },
      { key: 'fluency', label: 'Fluency' },
      { key: 'intelligibility_pct', label: 'Intelligibility (%)', type: 'number' },
    ],
  },
  {
    key: 'language',
    title: 'Language Assessment',
    fields: [
      { key: 'receptive', label: 'Receptive language', multiline: true },
      { key: 'expressive', label: 'Expressive language', multiline: true },
      { key: 'pragmatics', label: 'Pragmatics / social communication', multiline: true },
      { key: 'mlu', label: 'MLU (mean length of utterance)', type: 'number' },
      { key: 'aac_notes', label: 'AAC / multimodal communication', multiline: true },
    ],
  },
  {
    key: 'play_literacy',
    title: 'Play & Literacy',
    fields: [
      { key: 'play_skills', label: 'Play skills', multiline: true },
      { key: 'phonological_awareness', label: 'Phonological awareness / emergent literacy', multiline: true },
    ],
  },
  {
    key: 'summary',
    title: 'Summary & Clinical Impressions',
    fields: [
      { key: 'summary', label: 'Summary', multiline: true },
    ],
  },
  {
    key: 'recommendations',
    title: 'Recommendations',
    fields: [
      { key: 'services', label: 'Services recommended (frequency, duration, setting)', multiline: true },
      { key: 'goals_proposed', label: 'Goals proposed (one per line)', multiline: true, type: 'list' },
      { key: 'classroom_strategies', label: 'Classroom strategies for teachers', multiline: true },
      { key: 'home_carryover', label: 'Home carryover suggestions', multiline: true },
      { key: 'further_assessment', label: 'Further assessment needed', multiline: true },
    ],
  },
  {
    key: 'examiner',
    title: 'Examiner',
    fields: [
      { key: 'name', label: 'Name' },
      { key: 'credentials', label: 'Credentials (e.g. CCC-SLP)' },
      { key: 'license', label: 'License number' },
    ],
  },
];

/** Read a nested value from the eval data blob. */
function getNested(data: InitialEvaluation, section: string, key: string): string {
  const sec = data[section as EvalSectionKey];
  if (sec == null || typeof sec !== 'object') return '';
  const val = (sec as Record<string, unknown>)[key];
  if (val == null) return '';
  if (Array.isArray(val)) return val.join('\n');
  return String(val);
}

/** Write a nested value into the eval data blob (immutable). */
function setNested(
  data: InitialEvaluation,
  section: EvalSectionKey,
  key: string,
  value: string,
): InitialEvaluation {
  const sec = { ...(data[section] as Record<string, unknown> | undefined ?? {}) };
  if (value === '') {
    delete sec[key];
  } else if (key === 'goals_proposed') {
    sec[key] = value.split('\n').filter((l) => l.trim() !== '');
  } else if (key === 'intelligibility_pct' || key === 'mlu') {
    const n = parseFloat(value);
    if (!isNaN(n)) sec[key] = n;
    else delete sec[key];
  } else {
    sec[key] = value;
  }
  const result = { ...data };
  if (Object.keys(sec).length === 0) {
    delete result[section];
  } else {
    (result as Record<string, unknown>)[section] = sec;
  }
  return result;
}

export function InitialEvalForm({
  initialData,
  onSave,
  saving,
  saved,
}: Props): ReactElement {
  const [data, setData] = useState<InitialEvaluation>(initialData ?? {});
  const [dirty, setDirty] = useState(false);

  function update(section: EvalSectionKey, key: string, value: string): void {
    setData((prev) => setNested(prev, section, key, value));
    setDirty(true);
  }

  async function handleSave(): Promise<void> {
    await onSave(data);
    setDirty(false);
  }

  return (
    <div className="eval-form">
      {SECTIONS.map((section) => (
        <section key={section.key} className="card eval-section">
          <h3 className="onboarding-eyebrow">{section.title}</h3>
          {section.fields.map((field) => (
            <label key={field.key} className="eval-field">
              <span className="eval-field-label">{field.label}</span>
              {field.multiline ? (
                <textarea
                  value={getNested(data, section.key, field.key)}
                  onChange={(e) => update(section.key, field.key, e.target.value)}
                  rows={3}
                />
              ) : (
                <input
                  type={field.type === 'number' ? 'number' : 'text'}
                  value={getNested(data, section.key, field.key)}
                  onChange={(e) => update(section.key, field.key, e.target.value)}
                />
              )}
            </label>
          ))}
        </section>
      ))}

      <div className="form-actions">
        <button
          type="button"
          className="snap-button"
          disabled={saving || !dirty}
          onClick={() => void handleSave()}
        >
          {saving ? 'Saving…' : 'Save evaluation'}
        </button>
        {saved && !dirty && (
          <p className="tagline" style={{ color: 'var(--accent)' }}>
            <CheckIcon size={14} aria-hidden="true" /> Evaluation saved
          </p>
        )}
      </div>
    </div>
  );
}
